import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, Prompt, useParams } from "react-router-dom/cjs/react-router-dom";

import { Container, Row, Col, Card, Form, Button, Alert, Badge } from "react-bootstrap";

import useFetch from "../../../hooks/fetchMSSQL-hook";
import { API_URL } from "../../../config/config";
import { NavigationContext } from "../../../context/navigation-context";
import { NotificationManager } from "react-notifications";
import TagsFilter from "../../../components/Modals/TagsFilter";
import sampleProduct from "../../../assets/images/sample-product.svg";
import { useHistory } from 'react-router-dom';

const normPath = (p) => (p ? String(p).replace(/\\/g, "/") : p);

const MAX_TEXT_LEN = 249;
const MAX_DESC_LEN = 2000;

const SkeletonLine = ({ w = "70%", h = 12, radius = 6 }) => (
  <div style={{ width: w, height: h, borderRadius: radius, background: "rgba(0,0,0,.08)" }} />
);

const validateSettings = (s, initialDescription = "") => {
  const e = {};
  const nameRaw = String(s.Name || "");
  const name = nameRaw.trim();
  const code = String(s.Code || "").trim();
  const vat = String(s.VATCode || "").trim();
  const vatId = String(s.VatId || "").trim();
  const desc = String(s.Description || "");
  const priceRaw = s.LastPriceNetto;
  const price = priceRaw === "" || priceRaw === null || priceRaw === undefined ? null : Number(priceRaw);

  if (!name) e.Name = "Nazwa jest wymagana";
  else if (nameRaw.length > MAX_TEXT_LEN) e.Name = `Max ${MAX_TEXT_LEN} znaków`;
  else if ((/ {2,}/g).test(nameRaw)) e.Name = "Nie możesz użyć dwóch spacji pod rząd";
  if (price !== null && (!Number.isFinite(price) || price < 0)) e.LastPriceNetto = "Niepoprawna cena";
  const descriptionChanged = desc !== String(initialDescription || "");
  if (descriptionChanged && desc.length > MAX_DESC_LEN) e.Description = `Max ${MAX_DESC_LEN} znaków`;
  if (process.env.REACT_APP_LOCAL_DB === "true") {
    if (!vatId) e.VatId = "Wybierz VAT";
  } else {
    if (!code) e.Code = "Kod jest wymagany";
    else if (code.length > MAX_TEXT_LEN) e.Code = `Max ${MAX_TEXT_LEN} znaków`;
    if (!vat) e.VATCode = "Wybierz VAT";
    const isIng = Number(s.IsIngredient) ? 1 : 0;
    if (isIng) {
      const qtyRaw = s.IngredientQuantity;
      const qty = qtyRaw === "" || qtyRaw === null || qtyRaw === undefined ? null : Number(qtyRaw);
      if (qty === null || !Number.isFinite(qty) || qty <= 0) e.IngredientQuantity = "Pole wymagane";
    }
  }
  return e;
};

function ImagePicker({ apiUrl, valuePath, file, previewUrl, onPick, onClear, disabled }) {
  const inputRef = useRef(null);

  const src = previewUrl ? previewUrl : valuePath ? `${apiUrl}/${normPath(valuePath)}` : null;

  return (
    <div className="w-100">
      <input ref={inputRef} type="file" accept="image/png" style={{ display: "none" }} onChange={onPick} disabled={disabled} />

      <div
        className="border rounded p-2 d-flex align-items-center"
        style={{ gap: 12, background: "#fff", opacity: disabled ? 0.75 : 1 }}
      >
        <div
          className="border rounded overflow-hidden d-flex align-items-center justify-content-center"
          style={{ width: 76, height: 76, background: "#f8f9fa", flex: "0 0 76px" }}
        >
          {src ? (
            <img
              src={src}
              alt="Podgląd"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => (e.currentTarget.src = sampleProduct)}
            />
          ) : (
            <div className="text-muted text-center" style={{ fontSize: 12 }}>
              Brak
            </div>
          )}
        </div>

        <div className="w-100" style={{ minWidth: 0 }}>
          <div className="align-items-center justify-content-between" style={{ gap: 10 }}>
            <div className="d-flex" style={{ gap: 8, flex: "0 0 auto" }}>
              <Button size="sm" variant="primary" disabled={disabled} onClick={() => inputRef.current?.click()}>
                <i className="fas fa-upload mr-2" />
                Wybierz
              </Button>

              <Button
                size="sm"
                variant="outline-secondary"
                disabled={disabled || !src}
                onClick={() => {
                  onClear();
                  if (inputRef.current) inputRef.current.value = "";
                }}
              >
                <i className="fas fa-times mr-2" />
                Wyczyść
              </Button>
            </div>

            <div className="text-muted" style={{ fontSize: 12 }}>
              Najlepiej kwadratowe PNG (np. 512x512).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Product() {
  const params = useParams();
  const ean = params.EAN ?? params.ean;
  const history = useHistory();

  const { fetchMssqlApi } = useFetch();
  const { setHeaderData } = useContext(NavigationContext);

  const [loading, setLoading] = useState(true);
  const isBusy = loading;

  const [dirtySettings, setDirtySettings] = useState(false);
  const [submitSettingsAttempted, setSubmitSettingsAttempted] = useState(false);

  const [submitCategoriesAttempted, setSubmitCategoriesAttempted] = useState(false);

  const [productId, setProductId] = useState(null);

  const [vats, setVats] = useState([]);
  const [deposits, setDeposits] = useState([]);

  const [categories, setCategories] = useState([]);

  const [tags, setTags] = useState([]);
  const [initialTagIds, setInitialTagIds] = useState([]);
  const [tagsOpen, setTagsOpen] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const [uiMsgSettings, setUiMsgSettings] = useState(null);
  const [uiMsgCategories, setUiMsgCategories] = useState(null);
  const [ingredientTypes, setIngredientTypes] = useState([]);

  const emptyForm = (eanVal) => ({
    EAN: eanVal ?? "",
    Name: "",
    Description: "",
    VATCode: "",
    LastPriceNetto: "",
    IngredientQuantity: null,
    IngredientType: "",
    IsIngredient: 0,
    Code: "",
    IsActive: 1,
    DepositId: null,
    Image: null,
  });

  const [formState, setFormState] = useState(() => emptyForm(ean || ""));
  const [initialDescription, setInitialDescription] = useState("");

  const revokePreview = (url) => {
    try {
      if (url) URL.revokeObjectURL(url);
    } catch (e) {}
  };

  useEffect(() => () => revokePreview(imagePreviewUrl), [imagePreviewUrl]);

  const uiErrors = useMemo(
    () => validateSettings(formState, initialDescription),
    [formState, initialDescription]
  );
  const showSettingsErrors = (dirtySettings || submitSettingsAttempted) && !isBusy;

  const nameErr = showSettingsErrors ? uiErrors.Name : null;
  const codeErr = showSettingsErrors ? uiErrors.Code : null;
  const vatErr = showSettingsErrors ? uiErrors.VATCode : null;
  const priceErr = showSettingsErrors ? uiErrors.LastPriceNetto : null;
  const qtyErr = showSettingsErrors ? uiErrors.IngredientQuantity : null;
  const descErr = showSettingsErrors ? uiErrors.Description : null;

  const canSaveSettings = useMemo(() => Object.keys(uiErrors).length === 0, [uiErrors]);

  const grossPrice = useMemo(() => {
    const rawNet = String(formState.LastPriceNetto ?? "").trim().replace(",", ".");
    if (!rawNet) return "";

    const net = Number(rawNet);
    if (!Number.isFinite(net)) return "";

    const isLocalDb = process.env.REACT_APP_LOCAL_DB === "true";
    const selectedVat = (vats || []).find((v) =>
      isLocalDb
        ? String(v?.VatId ?? "") === String(formState.VatId ?? "")
        : String(v?.Code ?? "") === String(formState.VATCode ?? "")
    );

    if (!selectedVat) return "";

    const vatLabel = String(selectedVat?.Name ?? selectedVat?.VATName ?? "");
    const percentMatch = vatLabel.match(/(-?\d+(?:[.,]\d+)?)\s*%/);

    let vatPercent = percentMatch
      ? Number(percentMatch[1].replace(",", "."))
      : NaN;

    if (!Number.isFinite(vatPercent)) {
      const rawVatValue =
        selectedVat?.Value ??
        selectedVat?.VATValue ??
        selectedVat?.Rate ??
        selectedVat?.Percent ??
        selectedVat?.Percentage;

      const numericVatValue = Number(String(rawVatValue ?? "").replace(",", "."));
      if (!Number.isFinite(numericVatValue)) return "";

      vatPercent = Math.abs(numericVatValue) > 100 ? numericVatValue / 100 : numericVatValue;
    }

    return (net * (1 + vatPercent / 100)).toFixed(2).replace(".", ",");
  }, [formState.LastPriceNetto, formState.VATCode, formState.VatId, vats]);

  const setField = (name) => (e) => {
    if (isBusy) return;
    const value = e?.target?.value ?? "";
    setDirtySettings(true);
    setUiMsgSettings(null);
    setFormState((p) => ({ ...p, [name]: value }));
  };

  const setNumericField = (name) => (e) => {
    if (isBusy) return;
    const value = e?.target?.value ?? "";
    setDirtySettings(true);
    setUiMsgSettings(null);
    setFormState((p) => ({ ...p, [name]: value }));
  };

  const toggleIngredient = () => {
    if (isBusy) return;

    setDirtySettings(true);
    setUiMsgSettings(null);

    setFormState((p) => {
      const next = p.IsIngredient ? 0 : 1;
      return {
        ...p,
        IsIngredient: next,
        DepositId: next ? null : p.DepositId,
        IngredientQuantity: next ? p.IngredientQuantity : null,
        IngredientType: next ? p.IngredientType : null,
      };
    });
  };
  const deleteProduct = () => {
    if (isBusy) return;
    fetchMssqlApi(`product/${ean}`, {method: "DELETE"}, () => history.push("/products"));
  };

  const toggleActiveLocal = () => {
    if (isBusy) return;
    setDirtySettings(true);
    setUiMsgSettings(null);
    setFormState((p) => ({ ...p, IsActive: Number(p.IsActive) ? 0 : 1 }));
  };

  const clearImage = () => {
    if (isBusy) return;
    revokePreview(imagePreviewUrl);
    setImageFile(null);
    setImagePreviewUrl(null);
    setDirtySettings(true);
    setUiMsgSettings(null);
    setFormState((p) => ({ ...p, Image: null }));
  };

  const onPickImage = (e) => {
    if (isBusy) return;

    const file = e.target.files?.[0] || null;
    revokePreview(imagePreviewUrl);

    if (!file) {
      setImageFile(null);
      setImagePreviewUrl(null);
      e.target.value = "";
      return;
    }

    if (file.type !== "image/png") {
      NotificationManager.error("Tylko PNG", "Błąd", 2500);
      setImageFile(null);
      setImagePreviewUrl(null);
      e.target.value = "";
      return;
    }

    setUiMsgSettings(null);
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setDirtySettings(true);
    e.target.value = "";
  };

  const loadTagsAsync = async (pid) => {
    if (!pid) {
      setTags([]);
      setInitialTagIds([]);
      return;
    }

    const allTags = await new Promise((resolve) => {
      fetchMssqlApi(
        "products-tags",
        { method: "GET", hideNotification: true },
        (resp) => resolve(resp?.data ?? resp ?? []),
        () => resolve([])
      );
    });

    const productTags = await new Promise((resolve) => {
      fetchMssqlApi(
        `product-tags?productId=${encodeURIComponent(pid)}`,
        { method: "GET", hideNotification: true },
        (resp) => resolve(resp?.data ?? resp ?? []),
        () => resolve([])
      );
    });

    const productTagIds = (productTags || []).map((t) => t.TagId);

    const parsed = (allTags || []).map((t) => {
      const parts = String(t.Name || "").split(" - ");
      return {
        tagId: t.TagId,
        category: parts[0] || "Inne",
        name: parts[1] || parts[0] || "Tag",
        isActive: productTagIds.includes(t.TagId),
        fullName: t.Name,
      };
    });

    const grouped = parsed.reduce((acc, cur) => {
      if (!acc[cur.category]) acc[cur.category] = [];
      acc[cur.category].push({ tagId: cur.tagId, name: cur.name, isActive: cur.isActive, fullName: cur.fullName });
      return acc;
    }, {});

    setTags(Object.keys(grouped).map((cat) => ({ label: cat, options: grouped[cat] })));
    setInitialTagIds(productTagIds);
  };

  const saveTagsAsync = async (pid) => {
    if (!pid) return;

    const current = tags.flatMap((g) => g.options).filter((o) => o.isActive).map((o) => o.tagId);

    const added = current.filter((id) => !initialTagIds.includes(id));
    const removed = initialTagIds.filter((id) => !current.includes(id));

    const ops = [];

    added.forEach((tagId) =>
      ops.push(
        new Promise((resolve) => {
          fetchMssqlApi(
            "product-tag",
            { method: "POST", data: { productId: pid, tagId }, hideNotification: true },
            () => resolve({ ok: true }),
            () => resolve({ ok: false })
          );
        })
      )
    );

    removed.forEach((tagId) =>
      ops.push(
        new Promise((resolve) => {
          fetchMssqlApi(
            "product-tag",
            { method: "DELETE", data: { productId: pid, tagId }, hideNotification: true },
            () => resolve({ ok: true }),
            () => resolve({ ok: false })
          );
        })
      )
    );

    await Promise.allSettled(ops);
    setInitialTagIds(current);
  };

  const decorateCategories = (list) => {
    const mapped = (list || []).map((c) => {
      const linked = c.CategoryProductId != null;
      const initialMain = linked && Number(c.IsMain) === 1;

      return {
        ...c,
        _initialLinked: linked,
        _linked: linked,
        _initialMain: initialMain,
        _isMain: initialMain,
      };
    });

    const linkedCategories = mapped.filter((c) => c._linked);
    const storedMain = linkedCategories.find((c) => c._initialMain);
    const fallbackMain = storedMain || linkedCategories[0];

    if (!fallbackMain) return mapped;

    return mapped.map((c) => ({
      ...c,
      _isMain:
        Boolean(c._linked)
        && String(c.CategoryId) === String(fallbackMain.CategoryId),
    }));
  };

  const dirtyCategories = useMemo(
    () =>
      (categories || []).some(
        (c) =>
          Boolean(c._linked) !== Boolean(c._initialLinked)
          || Boolean(c._isMain) !== Boolean(c._initialMain)
      ),
    [categories]
  );

  const linkedCount = useMemo(
    () => (categories || []).filter((c) => Boolean(c._linked)).length,
    [categories]
  );

  const mainCategoryCount = useMemo(
    () =>
      (categories || []).filter(
        (c) => Boolean(c._linked) && Boolean(c._isMain)
      ).length,
    [categories]
  );

  const categoriesValid = linkedCount > 0 && mainCategoryCount === 1;

  const toggleCategory = (c) => {
    if (isBusy) return;

    setUiMsgCategories(null);
    const id = String(c.CategoryId);

    setCategories((prev) => {
      const source = prev || [];
      const current = source.find((x) => String(x.CategoryId) === id);

      if (!current) return source;

      const willBeLinked = !Boolean(current._linked);

      let next = source.map((x) =>
        String(x.CategoryId) === id
          ? {
              ...x,
              _linked: willBeLinked,
              _isMain: willBeLinked ? Boolean(x._isMain) : false,
            }
          : x
      );

      const hasMain = next.some(
        (x) => Boolean(x._linked) && Boolean(x._isMain)
      );

      if (!hasMain) {
        const firstLinked = next.find((x) => Boolean(x._linked));

        if (firstLinked) {
          next = next.map((x) => ({
            ...x,
            _isMain:
              Boolean(x._linked)
              && String(x.CategoryId) === String(firstLinked.CategoryId),
          }));
        }
      }

      return next;
    });
  };

  const setMainCategory = (categoryId) => {
    if (isBusy) return;

    setUiMsgCategories(null);
    const id = String(categoryId);

    setCategories((prev) =>
      (prev || []).map((c) => {
        const isTarget = String(c.CategoryId) === id;

        return {
          ...c,
          // Ustawienie kategorii jako głównej automatycznie ją przypisuje.
          _linked: isTarget ? true : Boolean(c._linked),
          // Tylko jeden checkbox „Główna” może być zaznaczony naraz.
          _isMain: isTarget,
        };
      })
    );
  };

  const saveCategoriesAsync = async () => {
    const selectedCategories = (categories || [])
      .filter((c) => Boolean(c._linked))
      .map((c) => ({
        CategoryId: Number(c.CategoryId),
        IsMain: c._isMain ? 1 : 0,
      }))
      .filter((c) => Number.isFinite(c.CategoryId));

    const mainCount = selectedCategories.filter((c) => c.IsMain === 1).length;

    if (selectedCategories.length === 0) {
      throw new Error("Produkt musi mieć przypisaną co najmniej jedną kategorię");
    }

    if (mainCount !== 1) {
      throw new Error("Wybierz kategorię główną");
    }

    const eanValue = String(formState.EAN || ean || "");

    await new Promise((resolve, reject) => {
      fetchMssqlApi(
        "category-products",
        {
          method: "POST",
          hideNotification: true,
          data: {
            EAN: eanValue,
            CategoryIds: JSON.stringify(selectedCategories),
          },
        },
        resolve,
        reject
      );
    });

    setCategories((prev) =>
      (prev || []).map((c) => ({
        ...c,
        _initialLinked: Boolean(c._linked),
        _initialMain: Boolean(c._linked) && Boolean(c._isMain),
      }))
    );
  };

  const getProduct = () =>
    new Promise((resolve) => {
      fetchMssqlApi(
        `product/${encodeURIComponent(ean)}`,
        { method: "GET", hideNotification: true },
        (resp) => {
          const data = resp?.data ?? resp;
          const p = Array.isArray(data) ? data[0] : data;

          setProductId(p?.ProductId ?? null);

          const description = p?.Description ?? "";
          setInitialDescription(description);
          setFormState({
            EAN: p?.EAN ?? ean ?? "",
            Name: p?.Name ?? "",
            Description: description,
            VATCode: p?.VATCode ?? "",
            VatId: p?.VatId ?? "",
            LastPriceNetto: p?.LastPriceNetto ?? "",
            IngredientQuantity: p?.IngredientQuantity ?? null,
            IngredientType: p?.IngredientType ?? "",
            IsIngredient: Number(p?.IsIngredient ?? 0),
            Code: p?.Code ?? "",
            IsActive: Number(p?.IsActive ?? 1),
            DepositId: p?.DepositId ?? null,
            Image: p?.Image ? normPath(p.Image) : null,
          });

          resolve(p?.ProductId ?? null);
        },
        () => resolve(null)
      );
    });

  const getDeposits = () => fetchMssqlApi("deposits", { method: "POST", hideNotification: true, showLoader: false, data: { isActive: null } }, (resp) => {setDeposits(resp)});
  const getIngredientTypes = () => fetchMssqlApi("ingredient-types",{ method: "POST", hideNotification: true, data: { isActive: true }, showLoader: false }, (resp) => {setIngredientTypes(resp?.data ?? resp ?? [])});
  const getCategories = () => {fetchMssqlApi(`categories/${ean}`, { method: "GET", hideNotification: true }, (resp) => {setCategories(decorateCategories(resp || []))})};
  const getVats = () => {fetchMssqlApi("vats",{ method: "POST", hideNotification: true, data: { isActive: true }, showLoader: false }, (resp) => {setVats(resp?.data ?? resp ?? [])})};

  const loadAll = async () => {
    setLoading(true);

    setDirtySettings(false);
    setSubmitSettingsAttempted(false);
    setSubmitCategoriesAttempted(false);

    setUiMsgSettings(null);
    setUiMsgCategories(null);

    revokePreview(imagePreviewUrl);
    setImageFile(null);
    setImagePreviewUrl(null);

    setProductId(null);
    setTags([]);
    setInitialTagIds([]);
    setTagsOpen(false);

    setCategories([]);
    setInitialDescription("");
    setFormState(emptyForm(ean || ""));

    const ProductId = await getProduct();
    await getCategories();
    await getVats();
    if (process.env.REACT_APP_LOCAL_DB === "false") {
      await loadTagsAsync(ProductId);
      await getDeposits();
      await getIngredientTypes();
    } 
    setLoading(false);
  };

  useEffect(() => {
    setHeaderData({ text: "Podgląd produktu" });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (ean) loadAll();
    // eslint-disable-next-line
  }, [ean]);

  const handleSubmitSettings = async (e) => {
    e.preventDefault();
    setSubmitSettingsAttempted(true);
    setUiMsgSettings(null);

    if (!canSaveSettings) {
      setUiMsgSettings({ type: "err", text: "Popraw pola zaznaczone na czerwono" });
      return;
    }

    const fd = new FormData();

    fd.append("EAN", String(formState.EAN || ean || ""));
    fd.append("Code", String(formState.Code || "").trim());
    fd.append("Name", String(formState.Name || "").trim());
    fd.append("VatId", String(formState.VatId || "").trim());
    fd.append("VATCode", String(formState.VATCode || "").trim());
    fd.append("IsIngredient", String(Number(formState.IsIngredient) ? 1 : 0));

    const isIng = Number(formState.IsIngredient) ? 1 : 0;
    if (isIng) {
      fd.append("IngredientQuantity", String(formState.IngredientQuantity ?? ""));
      fd.append("IngredientType", String(formState.IngredientType || "").trim().toUpperCase());
    } else {
      if (formState.DepositId !== "" && formState.DepositId !== null && formState.DepositId !== undefined) {
        fd.append("DepositId", String(formState.DepositId));
      }
    }

    fd.append("LastPriceNetto", String(formState.LastPriceNetto ?? ""));
    fd.append("IsActive", String(Number(formState.IsActive) ? 1 : 0));
    fd.append("Description", String(formState.Description || ""));

    if (imageFile) fd.append("Image", imageFile);
    else if (formState.Image) fd.append("Image", String(formState.Image));
    else fd.append("Image", "");

    fetchMssqlApi(
      "product",
      { method: "PUT", data: fd, hideNotification: true },
      async () => {
        try {
          if (productId) await saveTagsAsync(productId);
        } catch (e2) {}

        NotificationManager.success("Zapisano ustawienia", "Sukces", 2000);

        setDirtySettings(false);
        setSubmitSettingsAttempted(false);

        revokePreview(imagePreviewUrl);
        setImageFile(null);
        setImagePreviewUrl(null);

        const ProductId = await getProduct();
        await loadTagsAsync(ProductId);
      },
      (err) => {
        const payload = err?.response?.data || err?.data || null;
        const msg = payload?.message || "Błąd zapisu";
        setUiMsgSettings({ type: "err", text: msg });
        NotificationManager.error(msg, "Błąd", 2500);
      }
    );
  };

  const handleCancelCategories = () => {
    if (isBusy || !dirtyCategories) return;

    setCategories((prev) =>
      (prev || []).map((c) => ({
        ...c,
        _linked: Boolean(c._initialLinked),
        _isMain: Boolean(c._initialLinked) && Boolean(c._initialMain),
      }))
    );
    setSubmitCategoriesAttempted(false);
    setUiMsgCategories(null);
  };

  const handleSubmitCategories = async (e) => {
    e.preventDefault();
    setSubmitCategoriesAttempted(true);
    setUiMsgCategories(null);

    if (!dirtyCategories) return;

    if (!categoriesValid) {
      const msg = linkedCount === 0
        ? "Produkt musi mieć przypisaną co najmniej jedną kategorię"
        : "Dokładnie jedna przypisana kategoria musi być kategorią główną";

      setUiMsgCategories({ type: "err", text: msg });
      return;
    }

    try {
      await saveCategoriesAsync();
      NotificationManager.success("Zapisano kategorie", "Sukces", 2000);
      setSubmitCategoriesAttempted(false);
      await getCategories();
    } catch (e2) {
      const msg = e2?.message || "Nie udało się zapisać kategorii";
      setUiMsgCategories({ type: "err", text: msg });
      NotificationManager.error(msg, "Błąd", 2500);
    }
  };

  const imgSrc = imagePreviewUrl ? imagePreviewUrl : formState.Image ? `${API_URL}/${normPath(formState.Image)}` : sampleProduct;

  return (
    <Container fluid className="py-2">
      <Prompt
        when={!isBusy && (dirtySettings || dirtyCategories)}
        message={"Istnieją niezapisane zmiany, czy na pewno chcesz kontynuować?"}
      />
      <div className="mb-1">
        <Link to="/products" className="text-decoration-none">
          <Button variant="link" disabled={isBusy} className="p-0">
            <i className="fas fa-arrow-left mr-2" />
            Wróć
          </Button>
        </Link>
      </div>
      <Row>
        <Col md={12} xl={4} className="px-1 mb-2 mb-xl-0">
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <strong>Ustawienia</strong>
            </Card.Header>

            <Form autoComplete="off" onSubmit={handleSubmitSettings}>
              <Card.Body>
                {uiMsgSettings && (
                  <Alert variant={uiMsgSettings.type === "ok" ? "success" : "danger"}>
                    {uiMsgSettings.text}
                  </Alert>
                )}

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">
                    EAN
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control size="sm" value={formState.EAN || ""} disabled />
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">
                    Nazwa
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      size="sm"
                      value={formState.Name}
                      onChange={setField("Name")}
                      disabled={isBusy}
                      isInvalid={!!nameErr}
                      maxLength={MAX_TEXT_LEN}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{nameErr}</Form.Control.Feedback>
                  </Col>
                </Form.Group>
                {process.env.REACT_APP_LOCAL_DB === "false" && (
                  <Form.Group as={Row} className="mb-2">
                    <Form.Label column sm={4} className="text-muted text-sm-right">
                      Kod dodatkowy
                    </Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        size="sm"
                        value={formState.Code ?? ""}
                        disabled={isBusy}
                        isInvalid={!!codeErr}
                        maxLength={MAX_TEXT_LEN}
                        onChange={(e) => {
                          if (isBusy) return;
                          const value = (String(e.target.value) || "");
                          setDirtySettings(true);
                          setUiMsgSettings(null);
                          setFormState((p) => ({ ...p, Code: value }));
                        }}
                        required
                      />
                      <Form.Control.Feedback type="invalid">{codeErr}</Form.Control.Feedback>
                    </Col>
                  </Form.Group>
                )}
                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">
                    Ostatnia cena netto (opcjonalne)
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      step="0.01"
                      value={formState.LastPriceNetto ?? ""}
                      onChange={setNumericField("LastPriceNetto")}
                      disabled={isBusy}
                      isInvalid={!!priceErr}
                    />
                    <Form.Control.Feedback type="invalid">{priceErr}</Form.Control.Feedback>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">
                    Cena brutto
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      size="sm"
                      type="text"
                      value={String(grossPrice || "").replace(".", ",")}
                      disabled
                    />
                  </Col>
                </Form.Group>
                {process.env.REACT_APP_LOCAL_DB === "false" && (
                  <Form.Group as={Row} className="mb-0">
                    <Form.Label column sm={4} className="text-muted text-sm-right">
                      Składnik
                    </Form.Label>
                    <Col sm={8} className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        checked={!!Number(formState.IsIngredient)}
                        disabled={isBusy}
                        onChange={toggleIngredient}
                        style={{
                          width: 15,
                          height: 15,
                          cursor: isBusy ? "not-allowed" : "pointer",
                          accentColor: "var(--primary)",
                        }}
                      />
                    </Col>
                  </Form.Group>
                )}
                {process.env.REACT_APP_LOCAL_DB === "false" && !!Number(formState.IsIngredient) && (
                  <>
                    <Form.Group as={Row} className="mb-2">
                      <Form.Label column sm={4} className="text-muted text-sm-right">
                        Ilość składnika
                      </Form.Label>
                      <Col sm={8}>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          step="0.01"
                          value={formState.IngredientQuantity ?? ""}
                          onChange={setNumericField("IngredientQuantity")}
                          disabled={isBusy}
                          isInvalid={!!qtyErr}
                        />
                        <Form.Control.Feedback type="invalid">{qtyErr}</Form.Control.Feedback>
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-2">
                      <Form.Label column sm={4} className="text-muted text-sm-right">
                        Typ składnika
                      </Form.Label>

                      <Col sm={8}>
                        <Form.Control
                          as="select"
                          size="sm"
                          value={formState.IngredientType ?? ""}
                          onChange={(e) => {
                            setField("IngredientType")(e);
                          }}
                          disabled={isBusy}
                          isInvalid={!!qtyErr}
                        >
                          <option value="">{isBusy ? " " : "Wybierz"}</option>

                          {isBusy ? (
                            <>
                              <option value="" disabled>{" "}</option>
                              <option value="" disabled>{" "}</option>
                            </>
                          ) : (
                    ingredientTypes.map((t) => (
                      <option key={t.Code} value={t.Code}>
                        {t.Name}
                      </option>
                    ))
                          )}
                        </Form.Control>

                        <Form.Control.Feedback type="invalid">
                          {qtyErr}
                        </Form.Control.Feedback>
                      </Col>
                    </Form.Group>
                  </>
                )}
                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">
                    Stawka VAT
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={process.env.REACT_APP_LOCAL_DB === "false" ? (formState.VATCode ?? "") : (formState.VatId ?? "")}
                      onChange={process.env.REACT_APP_LOCAL_DB === "false" ? setField("VATCode"): setField("VatId")}
                      disabled={isBusy}
                      isInvalid={!!vatErr}
                      required
                    >
                      <option value="">{isBusy ? " " : "Wybierz"}</option>
                      {isBusy ? (
                        <>
                          <option value="" disabled>{" "}</option>
                          <option value="" disabled>{" "}</option>
                        </>
                      ) : (
                        vats.map((v) => process.env.REACT_APP_LOCAL_DB === "false" ?
                          (
                            <option key={v.Code} value={v.Code}>
                              {v.Name}
                            </option>
                          ) : (
                            <option key={v.VatId} value={v.VatId}>
                              {v.Name}
                            </option>
                          )
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{vatErr}</Form.Control.Feedback>
                  </Col>
                </Form.Group>
                {process.env.REACT_APP_LOCAL_DB === "false" && !Number(formState.IsIngredient) && (
                  <Form.Group as={Row} className="mb-2">
                    <Form.Label column sm={4} className="text-muted text-sm-right">
                      Kaucja
                    </Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        as="select"
                        size="sm"
                        value={formState.DepositId ?? ""}
                        onChange={setField("DepositId")}
                        disabled={isBusy}
                      >
                        <option value="">{isBusy ? " " : "Wybierz"}</option>
                        {isBusy ? (
                          <>
                            <option value="" disabled>{" "}</option>
                            <option value="" disabled>{" "}</option>
                          </>
                        ) : (
                          deposits
                            .filter((d) => d.IsActive === 1 || String(d.DepositId) === String(formState.DepositId))
                            .map((d) => (
                              <option key={d.DepositId} value={d.DepositId}>
                                {d.Name} ({d.Price} zł)
                              </option>
                            ))
                        )}
                      </Form.Control>
                    </Col>
                  </Form.Group>
                )}

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">
                    Opis
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      size="sm"
                      className="custom-scroll"
                      maxLength={MAX_DESC_LEN}
                      value={formState.Description ?? ""}
                      onChange={setField("Description")}
                      disabled={isBusy}
                      isInvalid={!!descErr}
                    />
                    <Form.Control.Feedback type="invalid">{descErr}</Form.Control.Feedback>
                  </Col>
                </Form.Group>

                {process.env.REACT_APP_LOCAL_DB === "false" && !Number(formState.IsIngredient) && (
                  <Form.Group as={Row} className="mb-2">
                    <Form.Label column sm={4} className="text-muted text-sm-right">
                      Tagi
                    </Form.Label>
                    <Col sm={8}>
                      <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                        {isBusy ? (
                          <>
                            <Badge pill variant="light" className="px-3 py-2">
                              <SkeletonLine w="70px" h={10} />
                            </Badge>
                            <Badge pill variant="light" className="px-3 py-2">
                              <SkeletonLine w="90px" h={10} />
                            </Badge>
                          </>
                        ) : (
                          tags
                            .flatMap((g) => g.options)
                            .filter((o) => o.isActive)
                            .map((o) => (
                              <span key={o.tagId} className="btn btn-primary badge badge-pill px-2 py-2 font-weight-bold">
                                {o.fullName}
                              </span>
                            ))
                        )}

                        <button
                          type="button"
                          className="btn btn-outline-primary badge badge-pill px-3 py-2"
                          disabled={isBusy}
                          onClick={() => {
                            setTagsOpen(true);
                            setDirtySettings(true);
                            setUiMsgSettings(null);
                          }}
                        >
                          <i className="fas fa-plus" />
                        </button>
                      </div>
                    </Col>
                  </Form.Group>
                )}

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">
                    Zdjęcie
                  </Form.Label>
                  <Col sm={8}>
                    <ImagePicker
                      apiUrl={API_URL}
                      valuePath={formState.Image}
                      file={imageFile}
                      previewUrl={imagePreviewUrl}
                      onPick={onPickImage}
                      onClear={clearImage}
                      disabled={isBusy}
                    />
                  </Col>
                </Form.Group>
              </Card.Body>

              <Card.Footer className="bg-white d-flex justify-content-between" style={{ gap: 10 }}>
                {process.env.REACT_APP_LOCAL_DB === "true" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant={"outline-danger"}
                    onClick={deleteProduct}
                    disabled={isBusy}
                  >
                    Usuń produkt
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant={Number(formState.IsActive) ? "outline-danger" : "outline-success"}
                    onClick={toggleActiveLocal}
                    disabled={isBusy}
                  >
                    {Number(formState.IsActive) ? "Deaktywuj" : "Aktywuj"}
                  </Button>
                )} 
                

                <Button size="sm" type="submit" disabled={isBusy || !canSaveSettings || !dirtySettings}>
                  Zapisz ustawienia
                </Button>
              </Card.Footer>
            </Form>
          </Card>

          {tagsOpen && (
            <TagsFilter
              tags={tags}
              setTags={(t) => {
                setTags(t);
                setDirtySettings(true);
                setUiMsgSettings(null);
              }}
              multitag={true}
              handleClose={() => {
                setTagsOpen(false);
                setDirtySettings(true);
                setUiMsgSettings(null);
              }}
            />
          )}
        </Col>

        <Col md={12} xl={4} className="px-1 mb-2 mb-xl-0">
          <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex align-items-center justify-content-between">
              <div>
                <strong>Kategorie</strong> <Badge variant="primary">{linkedCount}</Badge>
              </div>
            </Card.Header>

            <Form onSubmit={handleSubmitCategories}>
              <Card.Body
                className="p-0 custom-scroll"
                style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "hidden" }}
              >
                {uiMsgCategories && (
                  <div className="p-2">
                    <Alert variant={uiMsgCategories.type === "ok" ? "success" : "danger"} className="mb-0">
                      {uiMsgCategories.text}
                    </Alert>
                  </div>
                )}

                <table className="table table-sm table-hover mb-0">
                  <thead
                    className="bg-white"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      boxShadow: "0 1px 0 rgba(0,0,0,.12)",
                    }}
                  >
                    <tr>
                      <th
                        className="align-middle px-3 py-2"
                        style={{ borderTop: 0 }}
                      >
                        Nazwa kategorii
                      </th>
                      <th
                        className="align-middle text-center py-2"
                        style={{ width: 90, borderTop: 0 }}
                      >
                        Główna
                      </th>
                      <th
                        className="align-middle text-center py-2"
                        style={{ width: 90, borderTop: 0 }}
                      >
                        Aktywna
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {isBusy ? (
                      Array.from({ length: 12 }).map((_, idx) => (
                        <tr key={`cat-skel-${idx}`}>
                          <td className="px-3 py-2">
                            <SkeletonLine w={`${55 + (idx % 4) * 8}%`} h={14} />
                          </td>
                          <td className="text-center align-middle">
                            <SkeletonLine w="16px" h={16} radius={3} />
                          </td>
                          <td className="text-center align-middle">
                            <SkeletonLine w="16px" h={16} radius={3} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      (categories || []).map((c) => {
                        const isSelected = Boolean(c._linked);
                        const isMain = isSelected && Boolean(c._isMain);

                        return (
                          <tr key={c.CategoryId}>
                            <td className="align-middle px-3 py-2 font-weight-bolder">
                              {c.Name}
                            </td>

                            <td className="align-middle text-center py-2">
                              <input
                                type="checkbox"
                                checked={isMain}
                                disabled={isBusy}
                                onChange={() => setMainCategory(c.CategoryId)}
                                title="Ustaw jako kategorię główną — wybranie innej automatycznie przełączy zaznaczenie"
                                style={{
                                  width: 15,
                                  height: 15,
                                  verticalAlign: "middle",
                                  cursor: isBusy ? "not-allowed" : "pointer",
                                  accentColor: "var(--primary)",
                                }}
                              />
                            </td>

                            <td className="align-middle text-center py-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isBusy}
                                onChange={() => toggleCategory(c)}
                                style={{
                                  width: 15,
                                  height: 15,
                                  verticalAlign: "middle",
                                  cursor: isBusy ? "not-allowed" : "pointer",
                                  accentColor: "var(--primary)",
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>

                {!isBusy && linkedCount > 0 && mainCategoryCount !== 1 && (
                  <small className="text-danger d-block text-center py-2">
                    Wybierz katogrię główną
                  </small>
                )}

                {submitCategoriesAttempted && !dirtyCategories && !isBusy && categoriesValid && (
                  <small className="text-muted d-block text-center py-2">Brak zmian w kategoriach</small>
                )}
              </Card.Body>

              <Card.Footer className="bg-white d-flex justify-content-between">
                <Button
                  size="sm"
                  type="button"
                  variant="secondary"
                  onClick={handleCancelCategories}
                  disabled={isBusy || !dirtyCategories}
                >
                  Anuluj
                </Button>

                <Button
                  size="sm"
                  type="submit"
                  disabled={isBusy || !dirtyCategories || !categoriesValid}
                >
                  Zapisz kategorie
                </Button>
              </Card.Footer>
            </Form>
          </Card>
        </Col>

        <Col md={12} xl={4} className="px-1">
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <strong>Podgląd zdjęcia</strong>
            </Card.Header>
            <Card.Body>
              <img
                alt="#"
                src={imgSrc}
                onError={(e) => (e.currentTarget.src = sampleProduct)}
                style={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  objectFit: "cover",
                  borderRadius: 10,
                  border: "1px solid rgba(0,0,0,.08)",
                  boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                  background: "#f8f9fa",
                  opacity: isBusy ? 0.85 : 1,
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}